import React, { useCallback, useEffect, useState } from 'react';
import Switch from 'react-switch';
import pt_BR from 'date-fns/locale/pt-BR';
import { useHistory } from 'react-router-dom';
import { format } from 'date-fns';
import { useTheme } from 'styled-components';

import api from '../../services/api';

import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/toast';

import { Container, Wrapper } from './styles';
import { SHORT_WEEK_DAYS } from '../../constants';

const SCHEDULES: string[] = [];

for (let i = 0; i < 24; i += 1) {
  for (let j = 0; j < 60; j += 15) {
    let hours: string | number = i;
    let minutes: string | number = j;

    if (hours < 10) {
      hours = `0${hours}`;
    }

    if (minutes < 10) {
      minutes = `0${minutes}`;
    }

    SCHEDULES.push(`${hours}:${minutes}`);
  }
}

SCHEDULES.push('23:59');

function moneyMask(value: string): string {
  return (Number(value.replace(/\D/g, '')) / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

const masks = {
  money: moneyMask,
};

export type MaskType = keyof typeof masks;

const CreateDiscountCoupon: React.FC = () => {
  const theme = useTheme();
  const history = useHistory();
  const { user, rotinas, findRotinas } = useAuth();
  const { addToast } = useToast();

  const [startHour, setStartHour] = useState('08:00');
  const [endHour, setEndHour] = useState('18:00');
  const [daysOption, setDaysOption] = useState(1);
  const [selectedDays, setSelectedDays] = useState(SHORT_WEEK_DAYS);
  const [active, setActive] = useState(true);
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [endDate, setEndDate] = useState('');
  const [discountType, setDiscountType] = useState(1);
  const [customerLimit, setCustomerLimit] = useState('');
  const [allLimit, setAllLimit] = useState('');
  const [discountValue, setDiscountValue] = useState('');
  const [minimumValue, setMinimumValue] = useState('');

  function handleDate(date: string): Date {
    const finalDate = new Date();

    const [year, month, day] = date.split('-');

    finalDate.setDate(Number(day));
    finalDate.setMonth(Number(month) - 1);
    finalDate.setFullYear(Number(year));

    return finalDate;
  }

  function mask(type: MaskType, value: string): string {
    const m = masks[type];
    return m(value);
  }

  function getNumberValue(value = '00,00'): number {
    return parseFloat(
      value
        .toString()
        .replace('R$', '')
        .replace(' ', '')
        .replace('.', '')
        .replace(',', '.'),
    );
  }

  function handleDays(days: string[], end: string, start: string): string {
    if (days.length === 0) {
      return 'Nenhum dia da semana este cupom está disponível';
    }

    if (days.length === 7) {
      return `Cupom válido todos os dias das ${start} até ${end}`;
    }

    if (days.length === 2 && days.includes('sab') && days.includes('dom')) {
      return `Cupom válido somente nos fins de semana das ${start} até ${end}`;
    }

    if (days.length === 5 && !days.includes('sab') && !days.includes('dom')) {
      return `Cupom válido somente nos dias da semana das ${start} até ${end}`;
    }

    return `Cupom válido somente ${days.join(', ')} das ${start} até ${end}`;
  }

  // eslint-disable-next-line consistent-return
  async function handleSubmit(): Promise<void> {
    try {
      if (!code) {
        return addToast({
          type: 'error',
          title: 'Ocorreu um erro',
          description: 'Insira uma chave para o cupom',
        });
      }

      if (!description) {
        return addToast({
          type: 'error',
          title: 'Ocorreu um erro',
          description: 'Insira uma descrição para o cupom',
        });
      }

      if (getNumberValue(discountValue) < 0) {
        return addToast({
          type: 'error',
          title: 'Ocorreu um erro',
          description: 'Insira um desconto válido',
        });
      }

      if (getNumberValue(minimumValue) < 0) {
        return addToast({
          type: 'error',
          title: 'Ocorreu um erro',
          description: 'Insira um desconto válido',
        });
      }

      if (getNumberValue(customerLimit) < 0) {
        return addToast({
          type: 'error',
          title: 'Ocorreu um erro',
          description: 'Insira um limite de clientes válido',
        });
      }

      if (getNumberValue(allLimit) < 0) {
        return addToast({
          type: 'error',
          title: 'Ocorreu um erro',
          description: 'Insira um limite geral válido',
        });
      }

      const parsedDays = {};

      SHORT_WEEK_DAYS.filter((weekDay) =>
        selectedDays.includes(weekDay),
      ).forEach((weekDay) => {
        Object.assign(parsedDays, {
          [weekDay]: {
            start: startHour,
            end: endHour,
          },
        });
      });

      await api.post('/cupom-desconto', {
        chave: code,
        ativo: active,
        compra_minima: getNumberValue(minimumValue) || 0,
        descricao: description,
        limite_cliente: getNumberValue(customerLimit) || 0,
        limite_geral: getNumberValue(allLimit) || 0,
        tipo_desconto: discountType,
        valor_desconto: getNumberValue(discountValue) || 0,
        empresa_id: user.empresa_id,
        dias: parsedDays,
        data_termino: endDate ? `${endDate}T12:00:00` : null,
      });

      addToast({
        type: 'success',
        title: 'Sucesso',
        description: 'Cupom cadastrado com sucesso',
      });

      history.push('/cupom-desconto');
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Ocorreu um erro',
        description: (err as any).response.data.message || 'Tente novamente',
      });
    }
  }

  const hasPermission = useCallback(
    (): boolean => findRotinas('promocoes')?.alterar || false,
    [findRotinas],
  );

  useEffect(() => {
    if (rotinas && !hasPermission()) {
      addToast({
        type: 'error',
        title: 'Não autorizado.',
        description: 'Você não tem permissão.',
      });
      history.push('/cupom-desconto');
    }
  }, [addToast, hasPermission, history, rotinas]);

  return (
    <Container>
      <Wrapper>
        <header>
          <div>
            <h1>Cupom de desconto</h1>
            <h3>
              Os cupons podem ser utilizados direto pelo seu cliente ou como uma
              forma de seus parceiros divulgarem um desconto.
            </h3>
          </div>
          <div style={{ display: 'flex' }}>
            <button
              type="button"
              className="outline"
              onClick={() => history.push('/cupom-desconto')}
            >
              <span>Descartar alterações</span>
            </button>
            <button type="button" onClick={handleSubmit}>
              <span>Adicionar</span>
            </button>
          </div>
        </header>
        <main>
          <div className="first-column">
            <div className="inputs-container">
              <h3>Código do cupom</h3>
              <input
                type="text"
                placeholder="Ex: CLIENTE10"
                value={code}
                onChange={(event) => setCode(event.target.value.toUpperCase())}
              />
            </div>
            <div className="inputs-container">
              <h3>Descrição do cupom</h3>
              <input
                type="text"
                placeholder="Ex: Cupom natal"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </div>
            <div className="inputs-container">
              <h3>Configuração do desconto</h3>
              <div className="inputs">
                <div className="input-content half">
                  <label htmlFor="discount_type">Tipo do desconto</label>
                  <select
                    name="discount_type"
                    id="discount_type"
                    value={discountType}
                    onChange={(event) => {
                      setDiscountType(Number(event.target.value));
                      setDiscountValue('');
                    }}
                  >
                    <option value="1">Valor fixo</option>
                    <option value="2">Percentual</option>
                  </select>
                </div>
                <div className="input-content half">
                  <label htmlFor="discount_value">Desconto</label>
                  <div className="input-wrapper">
                    {discountType === 1 && <span className="left">R$</span>}
                    <input
                      type={discountType === 1 ? 'text' : 'number'}
                      id="discount_value"
                      className="right"
                      value={discountValue}
                      step={1}
                      onChange={(event) => {
                        const { value } = event.target;

                        if (discountType === 1) {
                          setDiscountValue(mask('money', value).split(' ')[1]);

                          return;
                        }

                        setDiscountValue(value);
                      }}
                    />
                    {discountType === 2 && <span className="right">%</span>}
                  </div>
                </div>
                <div className="input-content half">
                  <label htmlFor="minimum_value">Compra mínima do pedido</label>
                  <div className="input-wrapper">
                    <span className="left">R$</span>
                    <input
                      type="text"
                      id="minimum_value"
                      className="left"
                      value={minimumValue}
                      onChange={(event) => {
                        const { value } = event.target;

                        setMinimumValue(mask('money', value).split(' ')[1]);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="inputs-container">
              <h3>Especificar dias válidos</h3>
              <div className="days">
                <button
                  type="button"
                  className={selectedDays.includes('seg') ? 'active' : ''}
                  onClick={() => {
                    if (selectedDays.includes('seg')) {
                      setSelectedDays((oldState) =>
                        oldState.filter((weekDay) => weekDay !== 'seg'),
                      );
                    } else {
                      setSelectedDays((oldState) => [...oldState, 'seg']);
                    }
                  }}
                >
                  SEG
                </button>
                <button
                  type="button"
                  className={selectedDays.includes('ter') ? 'active' : ''}
                  onClick={() => {
                    if (selectedDays.includes('ter')) {
                      setSelectedDays((oldState) =>
                        oldState.filter((weekDay) => weekDay !== 'ter'),
                      );
                    } else {
                      setSelectedDays((oldState) => [...oldState, 'ter']);
                    }
                  }}
                >
                  TER
                </button>
                <button
                  type="button"
                  className={selectedDays.includes('qua') ? 'active' : ''}
                  onClick={() => {
                    if (selectedDays.includes('qua')) {
                      setSelectedDays((oldState) =>
                        oldState.filter((weekDay) => weekDay !== 'qua'),
                      );
                    } else {
                      setSelectedDays((oldState) => [...oldState, 'qua']);
                    }
                  }}
                >
                  QUA
                </button>
                <button
                  type="button"
                  className={selectedDays.includes('qui') ? 'active' : ''}
                  onClick={() => {
                    if (selectedDays.includes('qui')) {
                      setSelectedDays((oldState) =>
                        oldState.filter((weekDay) => weekDay !== 'qui'),
                      );
                    } else {
                      setSelectedDays((oldState) => [...oldState, 'qui']);
                    }
                  }}
                >
                  QUI
                </button>
                <button
                  type="button"
                  className={selectedDays.includes('sex') ? 'active' : ''}
                  onClick={() => {
                    if (selectedDays.includes('sex')) {
                      setSelectedDays((oldState) =>
                        oldState.filter((weekDay) => weekDay !== 'sex'),
                      );
                    } else {
                      setSelectedDays((oldState) => [...oldState, 'sex']);
                    }
                  }}
                >
                  SEX
                </button>
                <button
                  type="button"
                  className={selectedDays.includes('sab') ? 'active' : ''}
                  onClick={() => {
                    if (selectedDays.includes('sab')) {
                      setSelectedDays((oldState) =>
                        oldState.filter((weekDay) => weekDay !== 'sab'),
                      );
                    } else {
                      setSelectedDays((oldState) => [...oldState, 'sab']);
                    }
                  }}
                >
                  SAB
                </button>
                <button
                  type="button"
                  className={selectedDays.includes('dom') ? 'active' : ''}
                  onClick={() => {
                    if (selectedDays.includes('dom')) {
                      setSelectedDays((oldState) =>
                        oldState.filter((weekDay) => weekDay !== 'dom'),
                      );
                    } else {
                      setSelectedDays((oldState) => [...oldState, 'dom']);
                    }
                  }}
                >
                  DOM
                </button>
              </div>

              <div className="option">
                <input
                  type="radio"
                  name="hours"
                  id="custom-hours"
                  checked={daysOption === 0}
                  onChange={() => setDaysOption(0)}
                />
                <label htmlFor="custom-hours">
                  Das{' '}
                  <select
                    disabled={daysOption !== 0}
                    value={startHour}
                    onChange={(event) => setStartHour(event.target.value)}
                    style={{
                      width: 'fit-content',
                      padding: '4px 9px',
                      margin: '0 5px',
                    }}
                  >
                    {SCHEDULES.map((schedule) => (
                      <option key={schedule}>{schedule}</option>
                    ))}
                  </select>{' '}
                  Até{' '}
                  <select
                    disabled={daysOption !== 0}
                    value={endHour}
                    onChange={(event) => setEndHour(event.target.value)}
                    style={{
                      width: 'fit-content',
                      padding: '4px 9px',
                      margin: '0 5px',
                    }}
                  >
                    {SCHEDULES.map((schedule) => (
                      <option key={schedule}>{schedule}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="option" style={{ marginTop: 15 }}>
                <input
                  type="radio"
                  name="hours"
                  id="business-hours"
                  checked={daysOption === 1}
                  onChange={() => {
                    setDaysOption(1);
                    setStartHour('08:00');
                    setEndHour('18:00');
                  }}
                />
                <label htmlFor="business-hours">
                  Usar horário comercial (08:00 às 18:00)
                </label>
              </div>
              <div className="option" style={{ marginTop: 15 }}>
                <input
                  type="radio"
                  name="hours"
                  id="extended-business-hours"
                  checked={daysOption === 2}
                  onChange={() => {
                    setDaysOption(2);
                    setStartHour('10:00');
                    setEndHour('22:00');
                  }}
                />
                <label htmlFor="extended-business-hours">
                  Usar horário comercial estendido (10:00 às 22:00)
                </label>
              </div>
            </div>
          </div>
          <div className="second-column">
            <div className="filled-container active">
              <Switch
                checked={active}
                onChange={setActive}
                id="active"
                checkedIcon={false}
                uncheckedIcon={false}
                onColor={theme.colors.main}
                height={16}
                width={35}
                handleDiameter={10}
              />
              <label htmlFor="active">{active ? 'Ativo' : 'Inativo'}</label>
            </div>
            <div className="inputs-container">
              <h3>Data de término</h3>
              <input
                type="date"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
              />
            </div>
            <div className="filled-container inputs-container">
              <h3>Resumo</h3>
              <ul>
                <li>
                  {discountType === 1 && 'R$ '}
                  {discountType === 1
                    ? discountValue || '00,00'
                    : getNumberValue(discountValue)}
                  {discountType === 2 && '%'} de desconto
                </li>
                <li>
                  {getNumberValue(minimumValue)
                    ? `Compra mínima de R$ ${minimumValue}`
                    : 'Compras de qualquer valor'}
                </li>
                <li>
                  {customerLimit
                    ? `Limite de uso de ${customerLimit} cupons por cliente`
                    : 'Sem limite de uso por cliente'}
                </li>
                <li>
                  {allLimit
                    ? `Limite de uso de ${allLimit} cupons no geral`
                    : 'Sem limite de uso no geral'}
                </li>
                <li>
                  {endDate
                    ? `Válido até ${format(
                        handleDate(endDate),
                        "dd 'de' MMMM 'de' yyyy",
                        {
                          locale: pt_BR,
                        },
                      )}`
                    : 'Sem data de validade'}
                </li>
                <li>{handleDays(selectedDays, endHour, startHour)}</li>
              </ul>
            </div>
            <div className="inputs-container">
              <h3>Limite de uso</h3>
              <div className="inputs">
                <div className="input-content">
                  <label htmlFor="customer_limit">Por cliente</label>
                  <input
                    type="number"
                    id="customer_limit"
                    min={0}
                    value={customerLimit}
                    onChange={(event) => setCustomerLimit(event.target.value)}
                  />
                </div>
                <div className="input-content">
                  <label htmlFor="all_limit">No geral</label>
                  <input
                    type="number"
                    id="all_limit"
                    min={0}
                    value={allLimit}
                    onChange={(event) => setAllLimit(event.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </main>
      </Wrapper>
    </Container>
  );
};

export default CreateDiscountCoupon;
